import IPermissionContextState from 'Models/PermissionContextModel'
import { useAppDispatch } from 'Store/selectors'
import { lazy, Suspense, type UIEvent, useRef, useState } from 'react'
import WithUserContext from 'Hoc/WithUserContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import VisitsPaths from 'Application/Visits/Constants/VisitsPaths'
import { clearNewVisit, setVisitScrollTop } from 'Application/Visits/Actions/reducer'
import Icon from 'Components/Icon'
import { useSettings } from 'Contexts/SettingsContext'
import Const from 'Utils/Consts'
import { Tab, TabList, TabPanel, Tabs } from 'Components/Tabs'
import { VisitsTabsEnum } from 'Application/Visits/Actions/enums'

const VisitsList = lazy(() => import('./VisitsList'))
const DraftVisitsList = lazy(() => import('./DraftVisitsList'))
const DispatchRequest = lazy(() => import('./DispatchRequest'))

type IProps = IPermissionContextState

const VisitsPage = (props: IProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [t] = useTranslation()
  const listRef = useRef<HTMLDivElement | null>(null)
  const { settings } = useSettings()

  const [searchParams, setSearchParams] = useSearchParams()

  const getInitialTab = (): VisitsTabsEnum => {
    const tabFromUrl = Number(searchParams.get('tab'))

    if (
      tabFromUrl === VisitsTabsEnum.Visits ||
      tabFromUrl === VisitsTabsEnum.Drafts ||
      tabFromUrl === VisitsTabsEnum.Dispatch
    ) {
      return tabFromUrl
    }

    return VisitsTabsEnum.Visits
  }

  const [tabValue, setTabValue] = useState<VisitsTabsEnum>(getInitialTab)

  const handleTabChange = (value: VisitsTabsEnum) => {
    setTabValue(value)
    setSearchParams({ tab: String(value) })
  }

  const handleScroll = (event: UIEvent<HTMLElement>) => {
    const target = event.target as HTMLElement
    dispatch(setVisitScrollTop(target.scrollTop))
  }

  const storeFileType = settings?.REACT_APP_STORE_FILE_TYPE ?? Const.env.REACT_APP_STORE_FILE_TYPE

  return (
    <>
      <div className="flex justify-between items-center h-16 px-6 shrink-0 py-4">
        <div className="flex items-center gap-2">
          <div className="text-lg font-bold pl-2">{t('VISITS')}</div>
        </div>
        <div
          className="flex items-center"
          onClick={() => {
            dispatch(clearNewVisit(storeFileType))
            navigate(
              props.context.HasRole('AI_TEST') ? VisitsPaths.FakeNewVisit : VisitsPaths.NewVisit,
            )
          }}
        >
          <button className="btn btn-primary flex gap-2 items-center">
            <Icon name="add" className="w-5 h-5" />
            {t('NEW_VISIT')}
          </button>
        </div>
      </div>

      <Tabs value={tabValue} onChange={handleTabChange}>
        <TabList className="tabs-list-provider">
          <Tab>{t('VISITS_TAB')}</Tab>
          <Tab>{t('DRAFT_VISITS_TAB')}</Tab>
          <Tab>{t('DISPATCH_REQUEST')}</Tab>
        </TabList>

        <div ref={listRef} className="flex flex-col grow tabs-provider" onScroll={handleScroll}>
          <TabPanel value={VisitsTabsEnum.Visits} className="flex-1 flex flex-col pt-0">
            <Suspense fallback={<div className="loading flex flex-1" />}>
              <VisitsList tabValue={tabValue} ref={listRef} />
            </Suspense>
          </TabPanel>

          <TabPanel value={VisitsTabsEnum.Drafts} className="flex-1 flex flex-col pt-0">
            <Suspense fallback={<div className="loading flex flex-1" />}>
              <DraftVisitsList tabValue={tabValue} ref={listRef} />
            </Suspense>
          </TabPanel>

          <TabPanel value={VisitsTabsEnum.Dispatch} className="flex-1 flex flex-col pt-0">
            <Suspense fallback={<div className="loading flex flex-1" />}>
              <DispatchRequest tabValue={tabValue} ref={listRef} />
            </Suspense>
          </TabPanel>
        </div>
      </Tabs>
    </>
  )
}

export default WithUserContext(VisitsPage)
